import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Proposal } from '../models/Proposal';
import { 
  getRestaurant, 
  proposalStartVoting, 
  proposalSetState, 
  setProposalPublic, 
  removeProposalMember, 
  decideProposal,
  inviteFriends,
  getUser,
  getPublicUser,
  listenToPublicUsers,
  listenToPublicUser,
  listenToProposal,
  proposalSetMeal,
  availableCustomMeals,
  createCustomMeal,
  getProposal
} from '../services/DataStore';
import { GoogleMap, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import RestaurantList from './RestaurantList';
import RestaurantView from './RestaurantView';
import { createRoot } from 'react-dom/client';
import VotingView from './VotingView';
import { IconConverter } from '../utils/IconConverter';
import CreateCustomMeal from './CreateCustomMeal';
import { debug } from '../utils/debug';
import { useNavigate } from 'react-router-dom';
import { useProposal } from '../contexts/ProposalContext';
import { GoogleMapsProvider } from '../contexts/GoogleMapsContext';
import { useDeepLink } from '../hooks/useDeepLink';

const GOOGLE_MAPS_API_KEY = 'AIzaSyA3LrsKwPtV1IMy2m_xLaapG8HXiUBmnHg';
const GOOGLE_MAPS_LIBRARIES = Object.freeze(['maps', 'marker', 'places', 'geocoding']);

// Constants
const SHARE_URL_BASE = 'https://didema.co/openproposal/action';

const containerStyle = {
  width: '100%',
  height: '100%'
};

// Create a separate MapComponent
const MapComponent = React.memo(({ 
  isLoaded, 
  proposal, 
  currentUser,
  setMap,
  onCleanup
}) => {
  const mapContainerRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const infoWindowRef = React.useRef(null);
  const isInitializedRef = React.useRef(false);
  const initPromiseRef = React.useRef(null);
  const mountRef = React.useRef(true);
  const initTimeoutRef = React.useRef(null);
  const containerReadyRef = React.useRef(false);
  const initLockRef = React.useRef(false);
  const popupRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const circleRef = React.useRef(null);
  const currentProposalRef = React.useRef(proposal);
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  // Update the proposal ref whenever it changes
  useEffect(() => {
    currentProposalRef.current = proposal;
  }, [proposal]);

  // Add render tracking
  useEffect(() => {
    debug.log('MapComponent rendered:', {
      proposalId: proposal?.id,
      isLoaded,
      isInitialized: isInitializedRef.current,
      hasMap: !!mapInstanceRef.current
    });
  });

  // Derive values from props
  const isOwner = currentUser?.uid === proposal?.owner;

  // Update debug logging for proposal prop changes
  useEffect(() => {
    debug.log('MapComponent received proposal update:', {
      id: proposal?.id,
      state: proposal?.getStateEnum?.(),
      decision: proposal?.decision,
      isInitialized: isInitializedRef.current,
      hasMap: !!mapInstanceRef.current
    });

    // If we have a map instance and the proposal changed, just update the view
    if (mapInstanceRef.current && isInitializedRef.current) {
      debug.log('MapComponent: Updating existing map for new proposal');
      calculateMapView(mapInstanceRef.current);
    }
  }, [proposal?.id, proposal?.getStateEnum?.(), proposal?.decision]);

  // Expose cleanup function to parent
  useEffect(() => {
    if (onCleanup) {
      onCleanup(() => {
        if (popupRef.current) {
          popupRef.current.setMap(null);
          popupRef.current = null;
          setIsPopupVisible(false);
        }
      });
    }
  }, [onCleanup]);

  // Define RestaurantPopup class inside the component
  const RestaurantPopup = useMemo(() => {
    if (!isLoaded) return null;

    return class extends window.google.maps.OverlayView {
      position;
      containerDiv;
      restaurant;
      onClose;

      constructor(position, restaurant, onClose) {
        super();
        this.position = position;
        this.restaurant = restaurant;
        this.onClose = onClose;

        // Create the container div
        this.containerDiv = document.createElement('div');
        this.containerDiv.style.position = 'absolute';
        this.containerDiv.style.width = '350px';
        this.containerDiv.style.height = '350px';
        this.containerDiv.style.transform = 'translate(-50%, -100%)';
        this.containerDiv.style.marginTop = '-80px';

        // Create the restaurant view container
        const restaurantView = document.createElement('div');
        restaurantView.style.width = '100%';
        restaurantView.style.height = '100%';
        restaurantView.style.borderRadius = '8px';
        restaurantView.style.overflow = 'hidden';
        restaurantView.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        this.containerDiv.appendChild(restaurantView);

        // Render the RestaurantView component
        const root = createRoot(restaurantView);
        root.render(
          <RestaurantView 
            restaurant={restaurant}
            onClose={onClose}
            isInfoWindow={true}
          />
        );

        // Stop clicks from bubbling up to the map
        RestaurantPopup.preventMapHitsAndGesturesFrom(this.containerDiv);
      }

      onAdd() {
        this.getPanes().floatPane.appendChild(this.containerDiv);
      }

      onRemove() {
        if (this.containerDiv.parentElement) {
          this.containerDiv.parentElement.removeChild(this.containerDiv);
        }
      }

      draw() {
        const divPosition = this.getProjection().fromLatLngToDivPixel(this.position);
        
        // Hide the popup when it is far out of view
        const display = Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
          ? 'block'
          : 'none';

        if (display === 'block') {
          this.containerDiv.style.left = divPosition.x + 'px';
          this.containerDiv.style.top = divPosition.y + 'px';
        }

        if (this.containerDiv.style.display !== display) {
          this.containerDiv.style.display = display;
        }
      }
    };
  }, [isLoaded]);

  const handleMarkerClick = useCallback((restaurant, marker) => {
    if (!mapInstanceRef.current) return;

    // Toggle popup visibility
    if (popupRef.current) {
      popupRef.current.setMap(null);
      popupRef.current = null;
      setIsPopupVisible(false);
    } else {
      // Create and show new popup
      const popup = new RestaurantPopup(
        marker.position,
        restaurant,
        () => {
          if (popupRef.current) {
            popupRef.current.setMap(null);
            popupRef.current = null;
            setIsPopupVisible(false);
          }
        }
      );
      popup.setMap(mapInstanceRef.current);
      popupRef.current = popup;
      setIsPopupVisible(true);
    }
  }, [RestaurantPopup]);

  const showPopup = useCallback((restaurant, position) => {
    if (!mapInstanceRef.current) return;

    // Clear any existing popup
    if (popupRef.current) {
      popupRef.current.setMap(null);
      popupRef.current = null;
    }

    // Create and show new popup
    const popup = new RestaurantPopup(
      position,
      restaurant,
      () => {
        if (popupRef.current) {
          popupRef.current.setMap(null);
          popupRef.current = null;
          setIsPopupVisible(false);
        }
      }
    );
    popup.setMap(mapInstanceRef.current);
    popupRef.current = popup;
    setIsPopupVisible(true);
  }, [RestaurantPopup]);

  const calculateMapView = useCallback(async (mapInstance) => {
    debug.log('MapComponent calculateMapView called:', {
      proposalState: proposal?.getStateEnum(),
      hasRestaurant: !!proposal,
      hasCoordinates: !!proposal?.coordinates,
      decision: proposal?.decision,
      hasMap: !!mapInstance,
      hasMarker: !!markerRef.current,
      hasCircle: !!circleRef.current,
      hasPopup: !!popupRef.current
    });

    if (!mapInstance || !proposal || !mountRef.current) {
      debug.log('MapComponent calculateMapView early return:', {
        hasMap: !!mapInstance,
        hasProposal: !!proposal,
        isMounted: mountRef.current
      });
      return;
    }

    if (proposal.getStateEnum() === Proposal.ProposalState.DECIDED && proposal.decision) {
      debug.log('MapComponent handling DECIDED state');
      try {
        // Hide circle if it exists
        if (circleRef.current) {
          circleRef.current.setMap(null);
        }

        // Load the restaurant data for the decided proposal
        const restaurant = await getRestaurant(proposal.decision);
        debug.log('MapComponent loaded restaurant data:', {
          id: restaurant?.id,
          name: restaurant?.name,
          hasCoordinates: !!restaurant?.coordinates
        });

        if (!restaurant || !restaurant.coordinates) {
          debug.error('MapComponent: No restaurant data or coordinates found');
          return;
        }

        const position = new window.google.maps.LatLng(
          restaurant.coordinates.latitude,
          restaurant.coordinates.longitude
        );

        // Create or update marker
        if (!markerRef.current) {
          debug.log('Creating new AdvancedMarkerElement');
          const { AdvancedMarkerElement } = await window.google.maps.importLibrary("marker");
          markerRef.current = new AdvancedMarkerElement({
            map: mapInstance,
            position: position,
            title: restaurant.name
          });
        } else {
          debug.log('Updating existing marker position');
          markerRef.current.position = position;
          markerRef.current.title = restaurant.name;
          markerRef.current.map = mapInstance;
        }

        // Create marker content
        const markerView = document.createElement('div');
        markerView.className = 'marker-view';
        markerView.style.width = '40px';
        markerView.style.height = '40px';
        markerView.style.position = 'relative';
        markerView.style.cursor = 'pointer';

        // Create marker background
        const markerBg = document.createElement('div');
        markerBg.style.width = '100%';
        markerBg.style.height = '100%';
        markerBg.style.backgroundColor = '#FFFFFF';
        markerBg.style.border = '2px solid #FF4B4B';
        markerBg.style.borderRadius = '8px';
        markerBg.style.overflow = 'hidden';
        markerView.appendChild(markerBg);

        // Add restaurant image if available
        if (restaurant.imageUrl) {
          const img = document.createElement('img');
          img.src = restaurant.imageUrl;
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          markerBg.appendChild(img);
        }

        // Update marker content
        markerRef.current.content = markerView;

        // Add click handler if not already added
        if (!markerRef.current.hasClickHandler) {
          markerRef.current.addListener('click', () => {
            handleMarkerClick(restaurant, markerRef.current);
          });
          markerRef.current.hasClickHandler = true;
        }

        // Calculate offset for better marker visibility
        const currentZoom = mapInstance.getZoom();
        const zoomFactor = Math.pow(2, currentZoom - 15);
        const offset = 0.008 / zoomFactor;
        
        const panPosition = new window.google.maps.LatLng(
          position.lat() + offset,
          position.lng()
        );

        // Use built-in smooth panning
        mapInstance.panTo(panPosition, { animation: window.google.maps.Animation.SMOOTH });
        
        // Smoothly set zoom level
        mapInstance.setZoom(15, { animation: window.google.maps.Animation.SMOOTH });

        // Show popup by default for decided proposals
        showPopup(restaurant, position);
      } catch (error) {
        debug.error('MapComponent error loading restaurant data:', error);
      }
    } else if (proposal.location) {
      debug.log('Creating circle for waiting/sorting state');
      // Hide marker if it exists
      if (markerRef.current) {
        markerRef.current.map = null;
      }

      const center = new window.google.maps.LatLng(
        proposal.location.latitude,
        proposal.location.longitude
      );

      // Create or update circle
      if (!circleRef.current) {
        debug.log('Creating new circle');
        circleRef.current = new window.google.maps.Circle({
          strokeColor: '#0000FF',
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: '#0000FF',
          fillOpacity: 0.2,
          map: mapInstance,
          center: center,
          radius: proposal.range
        });
      } else {
        debug.log('Updating existing circle');
        circleRef.current.setCenter(center);
        circleRef.current.setRadius(proposal.range);
        circleRef.current.setMap(mapInstance);
      }

      const bounds = circleRef.current.getBounds();
      
      // Use built-in smooth panning
      mapInstance.panTo(center, { animation: window.google.maps.Animation.SMOOTH });
      
      // Use built-in smooth bounds fitting
      mapInstance.fitBounds(bounds, {
        padding: 50,
        animation: window.google.maps.Animation.SMOOTH
      });
    }
  }, [proposal, handleMarkerClick, showPopup]);

  // Initialize map
  useEffect(() => {
    let isMounted = true;

    const initMap = async () => {
      if (initPromiseRef.current) {
        debug.log('MapComponent: Waiting for existing init promise');
        await initPromiseRef.current;
        return;
      }

      // Only initialize if we don't have a map instance
      if (!isLoaded || !mapContainerRef.current || isInitializedRef.current || !isMounted || mapInstanceRef.current) {
        debug.log('MapComponent: Skipping initialization - missing requirements or map exists:', {
          isLoaded,
          hasContainer: !!mapContainerRef.current,
          isInitialized: isInitializedRef.current,
          isMounted,
          hasMap: !!mapInstanceRef.current
        });
        return;
      }

      initPromiseRef.current = (async () => {
        try {
          debug.log('MapComponent: Starting map initialization');
          if (!isMounted) {
            if (mapInstanceRef.current) {
              debug.log('Cleaning up previous map instance');
              if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
              }
              if (circleRef.current) {
                circleRef.current.setMap(null);
              }
              if (infoWindowRef.current) {
                infoWindowRef.current.setMap(null);
              }
              mapInstanceRef.current = null;
            }
            return;
          }

          debug.log('Starting map initialization...');
          const { Map } = await window.google.maps.importLibrary("maps");
          const { AdvancedMarkerElement } = await window.google.maps.importLibrary("marker");
          
          if (!isMounted) return;

          // Determine initial center position
          let initialCenter;
          if (proposal?.getStateEnum() === Proposal.ProposalState.DECIDED && proposal?.decision) {
            // For decided proposals, we'll set the center after loading restaurant data
            initialCenter = { lat: 0, lng: 0 };
          } else if (proposal?.location) {
            // For undecided proposals, use the proposal location
            initialCenter = {
              lat: proposal.location.latitude,
              lng: proposal.location.longitude
            };
          } else {
            // Default to a reasonable location if no proposal data
            initialCenter = { lat: 37.7749, lng: -122.4194 }; // San Francisco
          }

          const mapInstance = new Map(mapContainerRef.current, {
            mapId: "4f8a2d2881ee203f",
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            center: initialCenter,
            zoom: 15
          });

          if (!isMounted) {
            mapInstance.setMap(null);
            return;
          }

          debug.log('Map instance created:', mapInstance);
          mapInstanceRef.current = mapInstance;
          setMap(mapInstance);
          isInitializedRef.current = true;

          // Add reset view button
          const resetButton = document.createElement('div');
          resetButton.className = 'absolute bottom-4 left-4 z-10';
          resetButton.innerHTML = `
            <button class="bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors" title="Reset View">
              <span class="material-symbols-outlined text-gray-600">restart_alt</span>
            </button>
          `;
          resetButton.addEventListener('click', async () => {
            if (!isMounted || !mapInstanceRef.current) return;
            
            // Get the current proposal from the ref
            const currentProposal = currentProposalRef.current;
            if (!currentProposal) return;

            debug.log('Reset button clicked for current proposal:', {
              id: currentProposal.id,
              state: currentProposal.getStateEnum(),
              decision: currentProposal.decision
            });
            
            // Calculate target view based on current proposal state
            let targetCenter, targetZoom;
            
            if (currentProposal.getStateEnum() === Proposal.ProposalState.DECIDED && currentProposal.decision) {
              // For decided proposals, we'll get the restaurant position
              try {
                const restaurant = await getRestaurant(currentProposal.decision);
                if (restaurant && restaurant.coordinates) {
                  targetCenter = new window.google.maps.LatLng(
                    restaurant.coordinates.latitude,
                    restaurant.coordinates.longitude
                  );
                  targetZoom = 15;
                }
              } catch (error) {
                debug.error('Error loading restaurant data for reset:', error);
              }
            } else if (currentProposal.location) {
              // For undecided proposals, use the proposal location
              targetCenter = new window.google.maps.LatLng(
                currentProposal.location.latitude,
                currentProposal.location.longitude
              );
              targetZoom = 12;
            }
            
            if (targetCenter) {
              // Combine pan and zoom into a single smooth animation
              mapInstanceRef.current.panTo(targetCenter);
              mapInstanceRef.current.setZoom(targetZoom);
            }
          });
          mapContainerRef.current.appendChild(resetButton);

          // Calculate initial map view
          calculateMapView(mapInstance);

        } catch (error) {
          debug.error('Error initializing map:', error);
        } finally {
          initPromiseRef.current = null;
        }
      })();

      await initPromiseRef.current;
    };

    initMap();

    return () => {
      debug.log('MapComponent: Cleaning up map instance');
      isMounted = false;
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (mapInstanceRef.current) {
        if (infoWindowRef.current) {
          infoWindowRef.current.setMap(null);
          infoWindowRef.current = null;
        }
        if (typeof mapInstanceRef.current.setMap === 'function') {
          mapInstanceRef.current.setMap(null);
        }
        mapInstanceRef.current = null;
      }
      isInitializedRef.current = false;
      initPromiseRef.current = null;
    };
  }, [isLoaded]); // Only reinitialize if isLoaded changes

  // Add effect to handle proposal state changes
  useEffect(() => {
    if (mapInstanceRef.current && proposal) {
      debug.log('MapComponent handling proposal state change:', {
        state: proposal.getStateEnum(),
        decision: proposal.decision,
        hasMap: !!mapInstanceRef.current,
        hasMarker: !!markerRef.current,
        hasCircle: !!circleRef.current,
        hasPopup: !!popupRef.current
      });

      // Always clean up all existing elements first
      debug.log('MapComponent cleaning up all existing elements due to state change');
      
      // Clear marker and its custom view
      if (markerRef.current) {
        debug.log('Cleaning up existing marker');
        // Remove the marker's content from the DOM
        const markerContent = markerRef.current.content;
        if (markerContent && markerContent.parentNode) {
          markerContent.parentNode.removeChild(markerContent);
        }
        markerRef.current.map = null;
        markerRef.current = null;
      }
      
      // Clear circle
      if (circleRef.current) {
        debug.log('Cleaning up existing circle');
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      
      // Clear popup
      if (popupRef.current) {
        debug.log('Cleaning up existing popup');
        popupRef.current.setMap(null);
        popupRef.current = null;
        setIsPopupVisible(false);
      }

      // Clear any existing info window
      if (infoWindowRef.current) {
        debug.log('Cleaning up existing info window');
        infoWindowRef.current.setMap(null);
        infoWindowRef.current = null;
      }

      // Then calculate the new view
      calculateMapView(mapInstanceRef.current);
    }
  }, [proposal?.getStateEnum(), proposal?.decision, calculateMapView]);

  // Also add cleanup in the unmount effect
  useEffect(() => {
    return () => {
      debug.log('MapComponent: Cleaning up map instance');
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (mapInstanceRef.current) {
        if (infoWindowRef.current) {
          infoWindowRef.current.setMap(null);
          infoWindowRef.current = null;
        }
        if (typeof mapInstanceRef.current.setMap === 'function') {
          mapInstanceRef.current.setMap(null);
        }
        mapInstanceRef.current = null;
      }
      isInitializedRef.current = false;
      initPromiseRef.current = null;
    };
  }, []);

  const createMarkerIcon = useCallback(async () => {
    if (!proposal?.imageUrl) {
      return;
    }

    const canvas = document.createElement('canvas');
    const size = 40;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = '#FF4B4B';
    ctx.lineWidth = 2;
    const radius = 8;
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(size - radius, 0);
    ctx.quadraticCurveTo(size, 0, size, radius);
    ctx.lineTo(size, size - radius);
    ctx.quadraticCurveTo(size, size, size - radius, size);
    ctx.lineTo(radius, size);
    ctx.quadraticCurveTo(0, size, 0, size - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    try {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = proposal.imageUrl;
      });

      const padding = 2;
      ctx.drawImage(img, padding, padding, size - (padding * 2), size - (padding * 2));
    } catch (error) {
      debug.error('Error loading restaurant image:', error);
    }
  }, [proposal?.imageUrl]);

  return (
    <div 
      ref={mapContainerRef} 
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        minHeight: '400px',
        overflow: 'hidden'
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Re-render if isLoaded changes or if proposal changes
  return prevProps.isLoaded === nextProps.isLoaded && 
         prevProps.proposal?.id === nextProps.proposal?.id &&
         prevProps.proposal?.getStateEnum?.() === nextProps.proposal?.getStateEnum?.() &&
         prevProps.proposal?.decision === nextProps.proposal?.decision;
});

const ProposalView = ({ proposal, onProposalUpdate }) => {
  // Auth context
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Map related state
  const [map, setMap] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [markerIcon, setMarkerIcon] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Restaurant and proposal state
  const [restaurant, setRestaurant] = useState(null);

  // UI state
  const [isBusy, setIsBusy] = useState(false);
  const [showAddFriendsPopup, setShowAddFriendsPopup] = useState(false);
  const [availableFriends, setAvailableFriends] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState({
    title: '',
    message: '',
    onConfirm: null
  });
  const [joinedUsers, setJoinedUsers] = useState([]);
  const [showResultsFor, setShowResultsFor] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [showVotingView, setShowVotingView] = useState(false);

  // Add new state for the updated proposal
  const [votingProposal, setVotingProposal] = useState(null);
  const [localProposal, setLocalProposal] = useState(proposal);
  const [cleanupMapPopup, setCleanupMapPopup] = useState(null);
  const [showCreateCustomMeal, setShowCreateCustomMeal] = useState(false);

  const isOwner = currentUser?.uid === proposal.owner;

  // Add render tracking
  useEffect(() => {
    debug.log('ProposalView rendered:', {
      proposalId: proposal?.id,
      state: proposal?.getStateEnum?.(),
      isLoading,
      isDataLoaded,
      isLoaded
    });
  });

  // Add debug logging for initial proposal
  useEffect(() => {
    debug.log('ProposalView received initial proposal:', {
      id: proposal?.id,
      state: proposal?.getStateEnum?.(),
      decision: proposal?.decision
    });
  }, []);

  // Set up local proposal listener
  useEffect(() => {
    let unsubscribe = null;

    if (proposal?.id) {
      debug.log('Setting up local proposal listener for:', proposal.id);
      unsubscribe = listenToProposal(proposal.id, (updatedProposal) => {
        if (updatedProposal) {
          debug.log('ProposalView received proposal update:', {
            id: updatedProposal.id,
            state: updatedProposal.getStateEnum(),
            decision: updatedProposal.decision,
            meal: updatedProposal.meal,
            previousState: localProposal?.getStateEnum?.(),
            previousDecision: localProposal?.decision,
            previousMeal: localProposal?.meal
          });
          setLocalProposal(updatedProposal);
          // Notify parent component of the update
          onProposalUpdate?.(updatedProposal);
        }
      });
    }

    return () => {
      if (unsubscribe) {
        debug.log('Cleaning up local proposal listener');
        unsubscribe();
      }
    };
  }, [proposal?.id, onProposalUpdate]);

  // Helper functions
  const showBusy = (busy) => {
    setIsBusy(busy);
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialogConfig({ title, message, onConfirm });
    setShowConfirmDialog(true);
  };

  const loadAvailableFriends = async () => {
    if (!currentUser || !proposal) return;

    try {
      // Get the current user's friends
      const user = await getUser(currentUser.uid);
      if (!user) return;

      const friendIds = user.getFriends();
      if (!friendIds || friendIds.length === 0) return;

      // Filter out friends that are already in the proposal
      const availableFriendIds = friendIds.filter(friendId => !proposal.joined.includes(friendId));
      if (availableFriendIds.length === 0) {
        setAvailableFriends([]);
        return;
      }

      // Load each friend's public data
      const friends = await Promise.all(
        availableFriendIds.map(async (friendId) => {
          try {
            const publicUser = await getPublicUser(friendId);
            return publicUser;
          } catch (error) {
            debug.error('Error loading friend:', error);
            return null;
          }
        })
      );

      // Filter out any null results and update state
      setAvailableFriends(friends.filter(friend => friend !== null));
    } catch (error) {
      debug.error('Error loading friends:', error);
    }
  };

  const handleShowResults = (userId) => {
    setShowResultsFor(userId);
  };

  const updateActionButton = () => {
    if (!localProposal) return null;

    const state = localProposal.getStateEnum();
    const userStatus = localProposal.getUserStatus(currentUser?.uid);

    switch (state) {
      case Proposal.ProposalState.WAITING:
        if (isOwner) {
          return {
            text: 'Start Voting',
            onClick: handleStartVoting,
            enabled: true
          };
        }
        return {
          text: 'Waiting',
          onClick: null,
          enabled: false
        };

      case Proposal.ProposalState.SORTING:
        if (userStatus !== Proposal.UserStatus.VOTED) {
          return {
            text: 'Vote',
            onClick: () => setShowVotingView(true),
            enabled: true
          };
        } else if (isOwner) {
          return {
            text: 'Decide',
            onClick: handleDecide,
            enabled: true
          };
        }
        return {
          text: 'Waiting',
          onClick: null,
          enabled: false
        };

      case Proposal.ProposalState.DECIDED:
        return {
          text: 'Results',
          onClick: () => handleShowResults('general'),
          enabled: true
        };

      default:
        return null;
    }
  };

  // Event handlers
  const handleStartVoting = async () => {
    if (!localProposal || isBusy) return;
    
    showBusy(true);
    try {
      const updatedProposal = await proposalStartVoting(localProposal.id);
      // Store the updated proposal and show voting window
      setVotingProposal(updatedProposal);
      setShowVotingView(true);
    } catch (error) {
      debug.error('Failed to start voting:', error);
      alert('Failed to start voting: ' + error.message);
    } finally {
      showBusy(false);
    }
  };

  const handleDecide = async () => {
    if (!localProposal || isBusy) return;
    
    showBusy(true);
    try {
      await decideProposal(localProposal.id);
      // The proposal listener will handle the UI update
    } catch (error) {
      debug.error('Failed to decide proposal:', error);
      alert('Failed to decide proposal: ' + error.message);
    } finally {
      showBusy(false);
    }
  };

  const handleLeaveEvent = async () => {
    if (!localProposal || isBusy) return;
    
    showConfirm(
      'Leave Event',
      'Are you sure you want to leave this event? If you are the owner the event will be deleted.',
      async () => {
        showBusy(true);
        try {
          await removeProposalMember(localProposal.id, currentUser.uid);
          // The parent component will handle navigation
        } catch (error) {
          debug.error('Failed to leave event:', error);
          alert('Failed to leave event: ' + error.message);
        } finally {
          showBusy(false);
        }
      }
    );
  };

  const handleDeleteEvent = async () => {
    if (!localProposal || isBusy) return;
    
    showConfirm(
      'Delete Event',
      'Are you sure you want to delete this event?',
      async () => {
        showBusy(true);
        try {
          await removeProposalMember(localProposal.id, currentUser.uid);
          // Navigate to home after successful deletion
          navigate('/', { replace: true });
        } catch (error) {
          debug.error('Failed to delete event:', error);
          alert('Failed to delete event: ' + error.message);
        } finally {
          showBusy(false);
        }
      }
    );
  };

  const handleRemoveFromEvent = async (user) => {
    if (!localProposal || isBusy) return;
    
    showConfirm(
      'Remove from Event',
      `Are you sure you want to remove ${user.displayname} from this event?`,
      async () => {
        showBusy(true);
        try {
          await removeProposalMember(localProposal.id, user.id);
          // The proposal listener will handle the UI update
        } catch (error) {
          debug.error('Failed to remove user from event:', error);
          alert('Failed to remove user from event: ' + error.message);
        } finally {
          showBusy(false);
        }
      }
    );
  };

  const handleResetRestaurantChoices = async () => {
    if (!localProposal || isBusy) return;
    
    showConfirm(
      'Reset Restaurant Choices',
      'Are you sure you want to throw out all votes and reset restaurant selections?',
      async () => {
        showBusy(true);
        try {
          // First set the proposal state to WAITING
          await proposalSetState(localProposal.id, Proposal.ProposalState.WAITING);
          
          // Clear any existing map elements
          if (map) {
            if (map.marker) {
              map.marker.setMap(null);
              map.marker = null;
            }
            if (map.circle) {
              map.circle.setMap(null);
              map.circle = null;
            }
          }
          
          // Clean up the popup using the callback
          if (cleanupMapPopup) {
            cleanupMapPopup();
          }
          
          // Recalculate map view for the waiting state
          if (map && localProposal.location) {
            const center = new window.google.maps.LatLng(
              localProposal.location.latitude,
              localProposal.location.longitude
            );
            
            // Create new circle for the waiting state
            map.circle = new window.google.maps.Circle({
              strokeColor: '#0000FF',
              strokeOpacity: 1,
              strokeWeight: 2,
              fillColor: '#0000FF',
              fillOpacity: 0.2,
              map: map,
              center: center,
              radius: localProposal.range
            });

            const bounds = map.circle.getBounds();
            map.panTo(center, { animation: window.google.maps.Animation.SMOOTH });
            map.fitBounds(bounds, {
              padding: 50,
              animation: window.google.maps.Animation.SMOOTH
            });
          }
        } catch (error) {
          debug.error('Failed to reset restaurant choices:', error);
          alert('Failed to reset restaurant choices: ' + error.message);
        } finally {
          showBusy(false);
        }
      }
    );
  };

  const handleShareEvent = async () => {
    if (!localProposal) return;

    if (!localProposal.isPublic) {
      showConfirm(
        'Confirm public share',
        'People you may not know will be allowed to join your event based on how you share. Open the event settings to change it back to a \'Friends only\' event at any time.',
        async () => {
          showBusy(true);
          try {
            await setProposalPublic(localProposal.id, true);
            const shareUrl = `${SHARE_URL_BASE}?type=openproposal&id=${localProposal.id}`;
            const shareText = `Join me for ${localProposal.getMealName()} at ${localProposal.generateDescription()}!\n\n${shareUrl}`;
            
            if (navigator.share) {
              try {
                await navigator.share({
                  title: 'Join my event',
                  text: shareText,
                  url: shareUrl
                });
              } catch (error) {
                debug.error('Error sharing:', error);
              }
            } else {
              // Fallback for browsers that don't support the Web Share API
              navigator.clipboard.writeText(shareText);
              alert('Share link copied to clipboard!');
            }
          } catch (error) {
            debug.error('Failed to make proposal public:', error);
            alert('Failed to share proposal: ' + error.message);
          } finally {
            showBusy(false);
          }
        }
      );
    } else {
      const shareUrl = `${SHARE_URL_BASE}?type=openproposal&id=${localProposal.id}`;
      const shareText = `Join me for ${localProposal.getMealName()} at ${localProposal.generateDescription()}!\n\n${shareUrl}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join my event',
            text: shareText,
            url: shareUrl
          });
        } catch (error) {
          debug.error('Error sharing:', error);
        }
      } else {
        // Fallback for browsers that don't support the Web Share API
        navigator.clipboard.writeText(shareText);
        alert('Share link copied to clipboard!');
      }
    }
  };

  const handleChangeMeal = async (meal) => {
    if (!localProposal || isBusy) return;
    
    debug.log('Changing meal for proposal:', {
      proposalId: localProposal.id,
      newMeal: {
        name: meal.getDescription(),
        id: meal.id,
        icon: meal.icon,
        categories: meal.categories
      }
    });
    
    showBusy(true);
    try {
      // Wait for the meal change to complete
      await proposalSetMeal(localProposal.id, meal);
      
      // Get the updated proposal directly
      const updatedProposal = await getProposal(localProposal.id);
      
      debug.log('Meal change completed, updated proposal:', {
        id: updatedProposal.id,
        meal: updatedProposal.meal,
        state: updatedProposal.getStateEnum()
      });

      // Update local state
      setLocalProposal(updatedProposal);
      
      // Notify parent of the update
      onProposalUpdate?.(updatedProposal);
    } catch (error) {
      debug.error('Failed to change meal:', error);
      alert('Failed to change meal: ' + error.message);
    } finally {
      showBusy(false);
    }
  };

  const handleMealCreated = async (customMeal) => {
    // Immediately update the UI with the created meal
    handleChangeMeal(customMeal);
    
    try {
      // Call createCustomMeal in the background
      const updatedUser = await createCustomMeal(customMeal.name, customMeal.toString());
      
      // Get the updated custom meals from the user
      const customMeals = updatedUser.getCustomMeals();
      const createdMeal = customMeals.find(meal => meal.name === customMeal.name);
      
      if (!createdMeal) {
        debug.error('Created meal not found in updated user data');
      }
    } catch (error) {
      debug.error('Error creating custom meal:', error);
      alert('Error saving custom meal: ' + error.message);
    }
  };

  // Load restaurant data when proposal changes
  useEffect(() => {
    const loadRestaurantData = async () => {
      // Only set loading state if we don't have the data yet
      if (!restaurant && localProposal.getStateEnum() === Proposal.ProposalState.DECIDED && localProposal.decision) {
        setIsLoading(true);
      }
      
      try {
        if (localProposal.getStateEnum() === Proposal.ProposalState.DECIDED && localProposal.decision) {
          const restaurant = await getRestaurant(localProposal.decision, (freshRestaurant) => {
            setRestaurant(freshRestaurant);
          });

          if (restaurant) {
            setRestaurant(restaurant);
          }
        } else if (localProposal.location) {
          // No need to set map center here, MapComponent will handle it
        }
      } catch (error) {
        debug.error('Error loading restaurant data:', error);
      } finally {
        setIsLoading(false);
        setIsDataLoaded(true);
      }
    };

    loadRestaurantData();
  }, [localProposal]);

  // Load joined users when proposal changes
  useEffect(() => {
    let unsubscribes = null;

    // Reset joined users when proposal changes
    setJoinedUsers([]);

    if (localProposal?.joined) {
      debug.log('Setting up listeners for joined users:', localProposal.joined);
      unsubscribes = listenToPublicUsers(localProposal.joined, (updatedUser) => {
        setJoinedUsers(prevUsers => {
          // Create a new array with the updated user
          const newUsers = prevUsers.filter(u => u.id !== updatedUser.id);
          newUsers.push(updatedUser);
          
          // Sort the users by display name
          return newUsers.sort((a, b) => {
            const nameA = a.displayname || '';
            const nameB = b.displayname || '';
            return nameA.localeCompare(nameB);
          });
        });
      });
    }

    return () => {
      if (unsubscribes) {
        debug.log('Cleaning up joined users listeners');
        unsubscribes();
      }
    };
  }, [localProposal?.id, localProposal?.joined]);

  // Load current user's public data
  useEffect(() => {
    let unsubscribe = null;

    if (currentUser?.uid) {
      debug.log('Setting up listener for current user:', currentUser.uid);
      unsubscribe = listenToPublicUser(currentUser.uid, (updatedUser) => {
        setCurrentUserData(updatedUser);
      });
    }

    return () => {
      if (unsubscribe) {
        debug.log('Cleaning up current user listener');
        unsubscribe();
      }
    };
  }, [currentUser?.uid]);

  const { isLoaded: jsApiLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    version: 'beta',
    mapIds: ['4f8a2d2881ee203f'],
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  useEffect(() => {
    setIsLoaded(jsApiLoaded);
  }, [jsApiLoaded]);

  return (
    <div className="h-full flex flex-col">
      {/* Header with event details */}
      <div className="bg-accent text-white p-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">{localProposal.generateDescription()}</h1>
            <div className="mt-1 flex items-center space-x-4 text-sm">
              <span className="flex items-center">
                <span className="material-symbols-outlined mr-1 text-lg">restaurant</span>
                {localProposal.getMealName()}
              </span>
              <span className="flex items-center">
                <span className="material-symbols-outlined mr-1 text-lg">calendar_today</span>
                {localProposal.getFormattedDate()}
              </span>
              <span className="flex items-center">
                <span className="material-symbols-outlined mr-1 text-lg">schedule</span>
                {new Date(localProposal.eventtime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
          <div className="relative">
            <button 
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              title="Settings"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const menu = document.createElement('div');
                menu.className = 'absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50';
                menu.style.top = `${rect.bottom + 5}px`;
                
                const items = [];
                
                if (isOwner) {
                  // Add Change Meal option when in WAITING state
                  if (localProposal.getStateEnum() === Proposal.ProposalState.WAITING) {
                    items.push({
                      label: 'Change Meal',
                      icon: 'restaurant',
                      onClick: () => {
                        const menu = document.createElement('div');
                        menu.className = 'absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg py-1';
                        menu.style.top = '100%';
                        menu.style.right = '0';
                        
                        // Add each meal to the menu
                        let mealCount = 0;
                        availableCustomMeals.forEach(meal => {
                          const button = document.createElement('button');
                          button.className = 'w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center text-black';
                          button.innerHTML = `
                            <span class="material-symbols-outlined mr-2 text-accent">${IconConverter.sfToMaterial(meal.icon)}</span>
                            <span>${meal.getDescription()}</span>
                          `;
                          button.onclick = () => {
                            handleChangeMeal(meal);
                            menu.remove();
                          };
                          menu.appendChild(button);
                          
                          // Add separator after the first 3 built-in meals
                          mealCount++;
                          if (mealCount === 3) {
                            const separator1 = document.createElement('div');
                            separator1.className = 'border-t border-gray-200 my-1';
                            menu.appendChild(separator1);
                          }
                        });

                        // Add separator before New option
                        const separator2 = document.createElement('div');
                        separator2.className = 'border-t border-gray-200 my-1';
                        menu.appendChild(separator2);

                        // Add New... option
                        const newButton = document.createElement('button');
                        newButton.className = 'w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center text-accent';
                        newButton.innerHTML = `
                          <span class="material-symbols-outlined mr-2">add_circle</span>
                          <span>New...</span>
                        `;
                        newButton.onclick = () => {
                          setShowCreateCustomMeal(true);
                          menu.remove();
                        };
                        menu.appendChild(newButton);

                        // Find the settings button's parent container
                        const settingsButton = document.querySelector('button[title="Settings"]');
                        if (settingsButton) {
                          const container = settingsButton.parentElement;
                          container.appendChild(menu);

                          const handleClickOutside = (e) => {
                            if (!container.contains(e.target)) {
                              menu.remove();
                              document.removeEventListener('click', handleClickOutside);
                            }
                          };

                          setTimeout(() => {
                            document.addEventListener('click', handleClickOutside);
                          }, 0);
                        }
                      }
                    });
                  }
                  
                  // Check if we should show "Reset Restaurant Choices"
                  const hasRestaurantPool = localProposal.restaurantpool && localProposal.restaurantpool.length > 0;
                  const isWithin24Hours = new Date(localProposal.eventtime) > new Date(Date.now() - 24 * 60 * 60 * 1000);
                  
                  if (hasRestaurantPool && isWithin24Hours) {
                    items.push({
                      label: 'Restart Restaurant Selection',
                      onClick: handleResetRestaurantChoices
                    });
                  }
                  
                  items.push({
                    label: 'Delete Event',
                    onClick: handleDeleteEvent
                  });
                } else {
                  items.push({
                    label: 'Leave Event',
                    onClick: handleLeaveEvent
                  });
                }
                
                items.forEach(item => {
                  const button = document.createElement('button');
                  button.className = 'w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center';
                  
                  if (item.icon) {
                    const icon = document.createElement('span');
                    icon.className = 'material-symbols-outlined mr-2';
                    icon.textContent = item.icon;
                    button.appendChild(icon);
                  }
                  
                  const label = document.createElement('span');
                  label.textContent = item.label;
                  button.appendChild(label);
                  
                  button.onclick = () => {
                    if (item.onClick) {
                      item.onClick();
                    }
                    if (menu.parentNode) {
                      document.body.removeChild(menu);
                    }
                  };
                  
                  menu.appendChild(button);
                });
                
                const handleClickOutside = (e) => {
                  if (menu.parentNode && !menu.contains(e.target) && e.target !== e.currentTarget) {
                    document.body.removeChild(menu);
                    document.removeEventListener('click', handleClickOutside);
                  }
                };
                
                document.body.appendChild(menu);
                setTimeout(() => {
                  document.addEventListener('click', handleClickOutside);
                }, 0);
              }}
            >
              <span className="material-symbols-outlined">settings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex min-h-0">
        {/* Left panel - Map */}
        <div className="flex-1 min-h-0 relative">
          {!isLoaded ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <MapComponent
              isLoaded={isLoaded}
              proposal={localProposal}
              currentUser={currentUser}
              setMap={setMap}
              onCleanup={setCleanupMapPopup}
            />
          )}
        </div>

        {/* Right panel - Participants and actions */}
        <div className="w-96 border-l border-gray-200 flex flex-col">
          {/* Participants section */}
          <div className="p-4 border-b border-gray-200 flex-1 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-800">Participants</h2>
              <button 
                className="p-2 text-accent hover:bg-gray-100 rounded-full transition-colors"
                title="Add People"
                onClick={() => {
                  setShowAddFriendsPopup(true);
                  loadAvailableFriends();
                }}
                disabled={isBusy}
              >
                <span className="material-symbols-outlined">person_add</span>
              </button>
            </div>
            <div className="space-y-2 flex-1">
              {joinedUsers.map(user => (
                <div 
                  key={user.id} 
                  className="group flex items-center space-x-3 p-2 hover:bg-accent hover:text-white rounded-lg bg-background relative"
                  onClick={(e) => {
                    // Only show dropdown for current user's actions
                    if (user.id === currentUser?.uid || isOwner) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const menu = document.createElement('div');
                      menu.className = 'absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg py-1 z-50';
                      menu.style.top = `${rect.bottom + 5}px`;
                      menu.style.right = '0';
                      
                      const items = [];

                      // Add status indicator
                      if (localProposal.getStateEnum() === Proposal.ProposalState.SORTING) {
                        const status = localProposal.getUserStatus(user.id);
                        items.push({
                          label: status === Proposal.UserStatus.VOTED ? 'Voted' : 
                                status === Proposal.UserStatus.NOTFINISHED ? 'Pending' : 'Not Started',
                          icon: status === Proposal.UserStatus.VOTED ? 'how_to_vote' : 
                                status === Proposal.UserStatus.NOTFINISHED ? 'pending' : 'schedule',
                          disabled: true
                        });
                      }

                      // Add show results option for decided state with voted status
                      if (localProposal.getStateEnum() === Proposal.ProposalState.DECIDED && 
                          localProposal.getUserStatus(user.id) === Proposal.UserStatus.VOTED) {
                        items.push({
                          label: 'Show Results',
                          icon: 'visibility',
                          onClick: () => handleShowResults(user.id)
                        });
                      }

                      // Add remove/leave option
                      if (isOwner && user.id !== currentUser?.uid) {
                        items.push({
                          label: 'Remove Diner',
                          icon: 'person_remove',
                          onClick: () => handleRemoveFromEvent(user)
                        });
                      } else if (!isOwner && user.id === currentUser?.uid) {
                        items.push({
                          label: 'Leave Event',
                          icon: 'exit_to_app',
                          onClick: () => showConfirm(
                            'Leave Event',
                            'Are you sure you want to leave this event?',
                            handleLeaveEvent
                          )
                        });
                      }

                      items.forEach(item => {
                        const button = document.createElement('button');
                        button.className = `w-full text-left px-4 py-2 text-sm flex items-center space-x-2 ${
                          item.disabled ? 'text-gray-400 cursor-default' : 'text-gray-700 hover:bg-gray-100'
                        }`;
                        button.disabled = item.disabled;
                        
                        if (item.icon) {
                          const icon = document.createElement('span');
                          icon.className = 'material-symbols-outlined text-lg';
                          icon.textContent = item.icon;
                          button.appendChild(icon);
                        }
                        
                        const label = document.createElement('span');
                        label.textContent = item.label;
                        button.appendChild(label);
                        
                        if (item.onClick) {
                          button.onclick = () => {
                            item.onClick();
                            if (menu.parentNode) {
                              document.body.removeChild(menu);
                            }
                          };
                        }
                        
                        menu.appendChild(button);
                      });

                      const handleClickOutside = (e) => {
                        if (menu.parentNode && !menu.contains(e.target) && e.target !== e.currentTarget) {
                          document.body.removeChild(menu);
                          document.removeEventListener('click', handleClickOutside);
                        }
                      };

                      document.body.appendChild(menu);
                      setTimeout(() => {
                        document.addEventListener('click', handleClickOutside);
                      }, 0);
                    }
                  }}
                >
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {user.avatarurl ? (
                        <img 
                          src={user.avatarurl} 
                          alt={user.displayname}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="material-symbols-outlined text-gray-500">account_circle</span>
                      )}
                    </div>
                    {user.id === localProposal.owner && (
                      <span className="material-symbols-outlined absolute -top-1 -right-1 text-yellow-500 bg-transparent rounded-full">
                        star
                      </span>
                    )}
                  </div>
                  <span className="text-black font-medium text-base flex-1 group-hover:text-white">
                    {user.displayname}
                  </span>
                  {/* Add status indicator */}
                  {localProposal.getStateEnum() === Proposal.ProposalState.SORTING && (
                    <span className="material-symbols-outlined text-accent group-hover:text-white">
                      {localProposal.getUserStatus(user.id) === Proposal.UserStatus.VOTED ? 'how_to_vote' : 
                       localProposal.getUserStatus(user.id) === Proposal.UserStatus.NOTFINISHED ? 'pending' : 'schedule'}
                    </span>
                  )}
                  {(user.id === currentUser?.uid || isOwner) && (
                    <span className="material-symbols-outlined text-accent group-hover:text-white">expand_more</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action button section */}
          <div className="mt-auto p-4 border-t border-gray-200">
            {(() => {
              const actionButton = updateActionButton();
              if (!actionButton) return null;
              
              return (
                <div className="relative">
                  <button 
                    className={`w-full px-4 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                      actionButton.enabled && !isBusy
                        ? 'bg-accent text-white hover:bg-opacity-90' 
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                    onClick={actionButton.onClick}
                    disabled={!actionButton.enabled || isBusy}
                  >
                    <span className="material-symbols-outlined">
                      {localProposal.getStateEnum() === Proposal.ProposalState.DECIDED ? 'restaurant' : 'how_to_vote'}
                    </span>
                    <span>{actionButton.text}</span>
                  </button>
                  {isBusy && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-75 rounded-lg">
                      <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Show Results Popup */}
      {showResultsFor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                {showResultsFor === 'general' ? 'Results' : 
                 showResultsFor === currentUser?.uid ? 'Your Results' : 
                 `Results of ${joinedUsers.find(u => u.id === showResultsFor)?.displayname || 'User'}`}
              </h2>
              <button
                onClick={() => setShowResultsFor(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <RestaurantList 
                restaurantIds={showResultsFor === 'general' ? localProposal.restaurantpool : localProposal.getUserSortedList(showResultsFor)} 
                title={showResultsFor === 'general' ? 'Results' : 
                       showResultsFor === currentUser?.uid ? 'Your Results' : 
                       `Results of ${joinedUsers.find(u => u.id === showResultsFor)?.displayname || 'User'}`}
                favoriteRestaurants={currentUserData?.favoriterestaurants || []}
                onClose={() => setShowResultsFor(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Friends Popup */}
      {showAddFriendsPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Add People</h2>
              <button 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                onClick={() => setShowAddFriendsPopup(false)}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <div className="mb-4">
              <button
                className="w-full bg-accent text-white px-4 py-2 rounded hover:bg-opacity-90 flex items-center justify-center space-x-2"
                onClick={handleShareEvent}
              >
                <span className="material-symbols-outlined">share</span>
                <span>Share Event</span>
              </button>
            </div>
            
            <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
              {availableFriends.map(friend => (
                <div 
                  key={friend.id}
                  className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg"
                >
                  <input
                    type="checkbox"
                    checked={selectedFriends.has(friend.id)}
                    onChange={(e) => {
                      const newSelected = new Set(selectedFriends);
                      if (e.target.checked) {
                        newSelected.add(friend.id);
                      } else {
                        newSelected.delete(friend.id);
                      }
                      setSelectedFriends(newSelected);
                    }}
                    className="w-4 h-4 text-accent"
                  />
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    {friend.avatarurl ? (
                      <img 
                        src={friend.avatarurl} 
                        alt={friend.displayname} 
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="material-symbols-outlined text-gray-500">account_circle</span>
                    )}
                  </div>
                  <span className="text-black font-medium text-base flex-1 group-hover:text-white">
                    {friend.displayname || friend.id}
                  </span>
                </div>
              ))}
              {availableFriends.length === 0 && (
                <div className="text-center text-gray-500 py-4">
                  No friends available to invite
                </div>
              )}
            </div>
            
            <div className="mt-4 flex justify-end space-x-2">
              <button
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                onClick={() => setShowAddFriendsPopup(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-accent text-white rounded hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={selectedFriends.size === 0 || isBusy}
                onClick={async () => {
                  showBusy(true);
                  try {
                    await inviteFriends(localProposal, selectedFriends);
                    setShowAddFriendsPopup(false);
                  } catch (error) {
                    debug.error('Failed to invite friends:', error);
                    alert('Failed to invite friends: ' + error.message);
                  } finally {
                    showBusy(false);
                  }
                }}
              >
                Invite Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-2">{confirmDialogConfig.title}</h2>
            <p className="text-gray-600 mb-6">{confirmDialogConfig.message}</p>
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-accent text-white rounded hover:bg-opacity-90"
                onClick={() => {
                  setShowConfirmDialog(false);
                  if (confirmDialogConfig.onConfirm) {
                    confirmDialogConfig.onConfirm();
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voting View */}
      {showVotingView && (
        <VotingView
          proposal={votingProposal || localProposal}
          onClose={() => {
            setShowVotingView(false);
            setVotingProposal(null);
          }}
          onVotingComplete={(sortedRestaurants) => {
            setShowVotingView(false);
            setVotingProposal(null);
            // If there's only one restaurant, automatically decide
            if (sortedRestaurants.length === 1) {
              handleDecide();
            }
          }}
        />
      )}

      {/* Create Custom Meal Popup */}
      {showCreateCustomMeal && (
        <CreateCustomMeal
          onClose={() => setShowCreateCustomMeal(false)}
          onMealCreated={handleMealCreated}
          location={proposal?.location}
        />
      )}
    </div>
  );
};

export default ProposalView; 